import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, articles, faqItems } from '@/mocks/articles';

const websiteSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '打屁股作文精选范文与写作技巧大全',
  url: 'https://zuowenjiqiao.com',
  description:
    '专为学生和家长提供打屁股作文相关范文、素材与写作思路，涵盖叙事结构、情感表达、细节描写等核心技巧。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://zuowenjiqiao.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
});

const orgSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '写作技巧大全',
  url: 'https://zuowenjiqiao.com',
  description:
    '专注中小学生作文写作指导，提供优质范文、专业写作技巧与丰富素材。',
});

const faqSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
});

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const featuredArticles = articles.filter((a) => a.featured);
  const latestArticles = articles.slice(0, 6);

  return (
    <>
      <title>打屁股作文精选范文与写作技巧大全 | 中小学生作文指导</title>
      <meta
        name="description"
        content="专为学生和家长提供打屁股作文相关范文、素材与写作思路，涵盖叙事结构、情感表达、细节描写等核心技巧，帮助孩子轻松完成以家庭教育、成长经历为主题的作文，提升语文写作能力。"
      />
      <meta
        name="keywords"
        content="打屁股作文,打屁股作文范文,打屁股作文素材,成长经历作文,家庭教育作文,叙事作文写作技巧,小学初中作文范文"
      />
      <link rel="canonical" href="https://zuowenjiqiao.com" />
      <meta property="og:title" content="打屁股作文精选范文与写作技巧大全 | 中小学生作文指导" />
      <meta
        property="og:description"
        content="专为学生和家长提供打屁股作文相关范文、素材与写作思路，涵盖叙事结构、情感表达、细节描写等核心技巧。"
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://zuowenjiqiao.com" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="打屁股作文精选范文与写作技巧大全" />
      <meta
        name="twitter:description"
        content="专为学生和家长提供打屁股作文相关范文、素材与写作思路。"
      />
      <script type="application/ld+json">{websiteSchema}</script>
      <script type="application/ld+json">{orgSchema}</script>
      <script type="application/ld+json">{faqSchema}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <header className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cozy%20Chinese%20study%20room%20filled%20with%20bookshelves%20and%20wooden%20furniture%2C%20afternoon%20sunlight%20streaming%20through%20large%20window%2C%20open%20notebooks%20on%20desk%2C%20calligraphy%20brush%20and%20ink%20stone%2C%20peaceful%20educational%20atmosphere%2C%20soft%20golden%20and%20cream%20tones&width=1800&height=800&seq=hero-home-01&orientation=landscape"
              alt="温暖的写作学习环境"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/35"></div>
            <div className="relative z-10 w-full text-center px-4 md:px-6">
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading text-white mb-4 max-w-3xl mx-auto leading-tight">
                打屁股作文精选范文与写作技巧大全
              </h1>
              <p className="text-sm md:text-lg text-white/85 max-w-2xl mx-auto mb-6 leading-relaxed">
                专为学生和家长提供成长经历、家庭教育主题作文的优质范文与写作技巧
                <br className="hidden sm:block" />
                涵盖叙事结构、情感表达、细节描写等核心方法，帮助孩子轻松写出好作文
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/fanwen-jingxuan"
                  className="px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  查看精选范文
                </Link>
                <Link
                  to="/category/xiezuo-silu"
                  className="px-6 py-3 rounded-full bg-white/15 text-white border border-white/30 font-medium text-sm hover:bg-white/25 transition-colors whitespace-nowrap"
                >
                  学习写作技巧
                </Link>
              </div>
            </div>
          </header>

          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                  精选范文推荐
                </h2>
                <p className="text-sm text-foreground-600 mt-2">
                  精选优质范文，感受真情实感与成长力量
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} variant="featured" />
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                  写作分类导航
                </h2>
                <p className="text-sm text-foreground-600 mt-2">
                  按主题分类浏览，快速找到你需要的写作指导
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center p-4 md:p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all"
                  >
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-200 transition-colors mb-3">
                      <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                    </div>
                    <h3 className="text-sm md:text-base font-heading text-foreground-900 group-hover:text-primary-600 transition-colors text-center">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1.5 text-center line-clamp-2">
                      {cat.description}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                    最新文章
                  </h2>
                  <p className="text-sm text-foreground-600 mt-1">
                    持续更新优质内容，陪伴孩子写作成长
                  </p>
                </div>
                <Link
                  to="/category/fanwen-jingxuan"
                  className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="space-y-3 md:space-y-4">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} variant="default" />
                ))}
              </div>
              <div className="mt-6 text-center sm:hidden">
                <Link
                  to="/category/fanwen-jingxuan"
                  className="inline-flex items-center gap-1 text-sm text-primary-600 font-medium"
                >
                  查看全部文章 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-600 mt-2">
                  关于作文写作指导的常见疑问解答
                </p>
              </div>
              <div className="space-y-3">
                {faqItems.map((item, index) => (
                  <div
                    key={index}
                    className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden"
                  >
                    <button
                      className="w-full flex items-center justify-between p-4 md:p-5 text-left hover:bg-background-100 transition-colors"
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    >
                      <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                        {item.question}
                      </span>
                      <i
                        className={`flex-shrink-0 text-foreground-400 transition-transform duration-200 ${
                          openFaq === index ? 'ri-subtract-line' : 'ri-add-line'
                        }`}
                      ></i>
                    </button>
                    {openFaq === index && (
                      <div className="px-4 md:px-5 pb-4 md:pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-3">
                开启写作提升之旅
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mb-6">
                浏览精选范文，学习写作技巧，让孩子的作文水平稳步提升
              </p>
              <Link
                to="/category/fanwen-jingxuan"
                className="inline-block px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                立即开始阅读
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}